import type { OfferType } from '@/types/BrandOffer.types';
import type { Filters } from './BrandOffersPage.types';

/** Route for the offer desk; used for auth redirect `state.from`. */
export const BRAND_OFFERS_PAGE_PATH = '/brand-offers' as const;

/** Base path for a single offer detail route (`/brand-offer/:id`). */
export const BRAND_OFFER_DETAIL_BASE = '/brand-offer' as const;

export const OFFER_DESK_HEADING_ID = 'offer-desk-heading' as const;

export const DEFAULT_FILTERS: Filters = {
  search: '',
  offerType: '',
  brand: '',
  isActive: '',
  ordering: '-priority',
};

/** Static copy for `BrandOffersPage` chrome and modals. */
export const BRAND_OFFERS_PAGE_COPY = {
  authChecking: 'Checking your session…',
  brandWord: 'SLAYD',
  brandModule: 'Offer desk',
  sessionUserTitle: 'Signed-in user',
  logoutTitle: 'End session',
  leave: 'Leave',
  reloadTitle: 'Reload this page of results',
  newOffer: 'New offer',
  heroEyebrow: 'Merchandising',
  heroTitleBefore: 'Shape ',
  heroTitleAccent: 'pricing rules',
  heroTitleAfter: ' by brand',
  heroLead:
    'Rank matters when two rules overlap — the higher rank wins for the same brand.',
  statTotalLabel: 'Total in catalog',
  statPageLabel: 'On this page',
  statLiveLabel: 'Live on this page',
  deleteModalEyebrow: 'Permanent removal',
  deleteModalCancel: 'Keep it',
  deleteModalConfirm: 'Remove offer',
  deleteModalConfirmLoading: 'Removing…',
  deleteModalBeforeStrong: 'Remove offer ',
  deleteModalAfterStrong: '? Shoppers will stop seeing it immediately.',
  brandsLoadError:
    'Could not load brands. Check your connection and try again.',
} as const;

export const PAGE_SIZE = 20;

/** Labels for list, filters, form pills, and preview footer */
export const OFFER_TYPE_LABELS: Record<OfferType, string> = {
  EVERYDAY_COUPONS: 'Everyday coupons',
  SALE: 'Sale',
  SALE_EXTRA_OFFERS: 'Sale + extra offers',
  COUPONS: 'Coupons',
  TIERED_COUPONS: 'Tiered coupons',
};

/** Ordered list of selectable offer kinds for filters and the create/edit form. */
export const OFFER_TYPES: OfferType[] = [
  'EVERYDAY_COUPONS',
  'SALE',
  'SALE_EXTRA_OFFERS',
  'COUPONS',
  'TIERED_COUPONS',
];

/** Badge / label hues aligned with `src/index.css` palette */
export const OFFER_TYPE_COLORS: Record<OfferType, string> = {
  SALE: '#c24b7a',
  SALE_EXTRA_OFFERS: '#a85d3a',
  COUPONS: '#2c2242',
  EVERYDAY_COUPONS: '#5f6b2e',
  TIERED_COUPONS: '#6b4082',
};

/**
 * Pre-defined banner templates for tiered offers. {value} is replaced at
 * render time with the flat rupees saved by the applicable tier rule.
 */
export const TIER_OFFER_TEMPLATES: { label: string; template: string }[] = [
  { label: 'Extra {value} off', template: 'Extra {value} off' },
  {
    label: 'Flat {value} off',
    template: 'Flat {value} off',
  },
  { label: 'Upto {value} off', template: 'Upto {value} off' },
  // { label: 'Get {value} cashback', template: 'Get {value} cashback' },
  // { label: '{value} off in cart', template: '{value} off in cart' },
];

export const ORDERING_OPTIONS = [
  { value: '-priority', label: 'Highest rank first' },
  { value: 'priority', label: 'Lowest rank first' },
  { value: '-created_at', label: 'Recently created' },
  { value: 'created_at', label: 'Oldest created' },
];

/** Legacy enum strings the backend may still return; map them to the new
 *  identifiers so the rest of the app only ever sees the new names. */
const LEGACY_OFFER_TYPE_MAP: Record<string, OfferType> = {
  EVERYDAY_VARIABLE: 'EVERYDAY_COUPONS',
  TIMED_SALE: 'SALE',
  TIMED_SALE_COUPON: 'SALE_EXTRA_OFFERS',
  SALE_COUPON: 'SALE_EXTRA_OFFERS',
  GENERIC_COUPON: 'COUPONS',
  CONDITIONAL_COUPON: 'TIERED_COUPONS',
};

const NEW_OFFER_TYPES = new Set<OfferType>(OFFER_TYPES);

/** API may still return removed types; normalize before storing in state. */
export function normalizeOfferType(raw: string): OfferType {
  if (NEW_OFFER_TYPES.has(raw as OfferType)) return raw as OfferType;
  return LEGACY_OFFER_TYPE_MAP[raw] ?? 'EVERYDAY_COUPONS';
}
